import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * El indice del centro de ayuda.
 *
 * Es la UNICA lista de paginas que existe. De aqui salen el menu lateral, el
 * buscador, las migas de pan y los enlaces de anterior/siguiente, asi que no
 * hay forma de que una pagina aparezca en el menu y no en la busqueda, ni de
 * que el orden del menu y el de «siguiente» digan cosas distintas.
 *
 * Cada pagina se carga por separado (`lazy`): entrar en la ayuda no debe
 * traerse las doce de golpe.
 *
 * Las `claves` son para el buscador: palabras que alguien escribiria sin saber
 * todavia como se llama lo que busca. «contrasena» lleva a la pagina de la
 * cuenta aunque esa palabra no sea el titulo de nada.
 */

export const DOCS_BASE = '/docs';

export interface PaginaDocs {
  slug: string;
  titulo: string;
  descripcion: string;
  claves: string[];
  ruta: string;
  Componente: LazyExoticComponent<ComponentType>;
}

export interface SeccionDocs {
  id: string;
  titulo: string;
  paginas: PaginaDocs[];
}

const pagina = (
  slug: string,
  titulo: string,
  descripcion: string,
  claves: string[],
  carga: () => Promise<{ default: ComponentType }>,
): PaginaDocs => ({
  slug,
  titulo,
  descripcion,
  claves,
  ruta: `${DOCS_BASE}/${slug}`,
  Componente: lazy(carga),
});

export const SECCIONES_DOCS: SeccionDocs[] = [
  {
    id: 'introduccion',
    titulo: 'Introduccion',
    paginas: [
      pagina(
        'inicio',
        'Que es el Ecosystem',
        'Para que sirve el portal y que encontraras en cada parte.',
        ['inicio', 'que es', 'ecosystem', 'portal', 'electrodavisan', 'empezar'],
        () => import('../pages/InicioPage'),
      ),
      pagina(
        'conceptos',
        'Conceptos basicos',
        'Aplicacion, acceso, rol, ticket y auditoria explicados de una vez.',
        ['conceptos', 'vocabulario', 'glosario', 'rol', 'ticket', 'acceso', 'diferencia'],
        () => import('../pages/ConceptosPage'),
      ),
    ],
  },
  {
    id: 'empezar',
    titulo: 'Empezar',
    paginas: [
      pagina(
        'primeros-pasos',
        'Entrar por primera vez',
        'Tu cuenta unica, el primer inicio de sesion y que hacer si algo falla.',
        ['entrar', 'iniciar sesion', 'login', 'contrasena', 'primera vez', 'acceso', 'sso'],
        () => import('../pages/PrimerosPasosPage'),
      ),
      pagina(
        'aplicaciones',
        'Abrir tus aplicaciones',
        'El lanzador, que significa cada estado y por que no ves alguna.',
        ['aplicaciones', 'abrir', 'lanzador', 'launcher', 'calendar', 'tablero', 'mantenimiento'],
        () => import('../pages/AplicacionesPage'),
      ),
    ],
  },
  {
    id: 'soporte',
    titulo: 'Soporte',
    paginas: [
      pagina(
        'crear-ticket',
        'Crear un ticket',
        'Como contar un problema para que se resuelva a la primera, con ejemplos.',
        ['ticket', 'crear', 'reportar', 'problema', 'fallo', 'error', 'ayuda', 'soporte'],
        () => import('../pages/CrearTicketPage'),
      ),
      pagina(
        'mis-tickets',
        'Seguir tus tickets',
        'Estados, prioridades, respuestas y cuando se cierra un ticket.',
        ['mis tickets', 'estado', 'prioridad', 'respuesta', 'seguimiento', 'cerrado', 'resuelto'],
        () => import('../pages/MisTicketsPage'),
      ),
    ],
  },
  {
    id: 'mesa',
    titulo: 'Mesa de ayuda',
    paginas: [
      pagina(
        'atender-tickets',
        'Atender tickets',
        'Para el equipo de soporte: bandejas, notas internas y cambios de estado.',
        ['soporte', 'atender', 'bandeja', 'asignar', 'nota interna', 'agente', 'cola'],
        () => import('../pages/AtenderTicketsPage'),
      ),
    ],
  },
  {
    id: 'administracion',
    titulo: 'Administracion',
    paginas: [
      pagina(
        'admin-aplicaciones',
        'Registrar aplicaciones',
        'Dar de alta una aplicacion en el lanzador y manejar sus estados.',
        ['registrar', 'aplicacion', 'alta', 'estado', 'orden', 'launch url', 'oidc'],
        () => import('../pages/AdminAplicacionesPage'),
      ),
      pagina(
        'admin-accesos',
        'Accesos: quien ve que',
        'Conceder por rol o por persona, y por que registrar no es publicar.',
        ['accesos', 'permisos', 'rol', 'conceder', 'revocar', 'visibilidad', 'quien ve'],
        () => import('../pages/AdminAccesosPage'),
      ),
      pagina(
        'admin-auditoria',
        'Auditoria',
        'Que queda registrado, como se lee y para que sirve de verdad.',
        ['auditoria', 'registro', 'historial', 'log', 'quien hizo', 'rastro'],
        () => import('../pages/AdminAuditoriaPage'),
      ),
    ],
  },
  {
    id: 'cuenta',
    titulo: 'Tu cuenta',
    paginas: [
      pagina(
        'cuenta',
        'Tu cuenta y tu sesion',
        'Perfil, contrasena, cerrar sesion en todas las aplicaciones a la vez.',
        ['cuenta', 'perfil', 'contrasena', 'cerrar sesion', 'salir', 'datos', 'keycloak'],
        () => import('../pages/CuentaPage'),
      ),
      pagina(
        'preguntas-frecuentes',
        'Preguntas frecuentes',
        'Las dudas que se repiten, con la respuesta corta.',
        ['preguntas', 'frecuentes', 'faq', 'dudas', 'problemas', 'ayuda'],
        () => import('../pages/PreguntasFrecuentesPage'),
      ),
    ],
  },
];

export interface PaginaConSeccion extends PaginaDocs {
  seccion: string;
  seccionId: string;
}

/** Todas las paginas en el orden del menu. El orden de «anterior/siguiente». */
export const PAGINAS_DOCS: PaginaConSeccion[] = SECCIONES_DOCS.flatMap((seccion) =>
  seccion.paginas.map((p) => ({ ...p, seccion: seccion.titulo, seccionId: seccion.id })),
);

const POR_SLUG = new Map(PAGINAS_DOCS.map((p) => [p.slug, p]));

export const paginaPorSlug = (slug: string | undefined): PaginaConSeccion | null =>
  (slug ? POR_SLUG.get(slug) : undefined) ?? null;

/**
 * La anterior y la siguiente, atravesando secciones.
 *
 * Se cruza el limite de seccion a proposito: quien va leyendo seguido espera
 * continuar, no chocar con un final que no anuncio nadie.
 */
export function vecinas(slug: string | undefined): {
  anterior: PaginaConSeccion | null;
  siguiente: PaginaConSeccion | null;
} {
  const i = PAGINAS_DOCS.findIndex((p) => p.slug === slug);
  if (i === -1) return { anterior: null, siguiente: null };
  return {
    anterior: i > 0 ? PAGINAS_DOCS[i - 1] : null,
    siguiente: i < PAGINAS_DOCS.length - 1 ? PAGINAS_DOCS[i + 1] : null,
  };
}

/** La primera pagina, a la que lleva la portada cuando alguien entra sin mas. */
export const PAGINA_INICIAL = PAGINAS_DOCS[0];
